import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  createMonitoringIcon,
  getMonitoringTypeFromToken,
  MONITORING_BADGE_STYLES,
  type MonitoringType,
} from "@/constants/monitoring";
import { DashboardCard } from "../../Dashboard/DashboardCard";
import { Pagination } from "@/components/Common/Pagination";
import { useGetClientActivityLog } from "../../../api/client.queries";
import { getActivityStatusColor } from "../../../utils/clientUtils";
import { formatActivityTimestamp } from "@/lib/dateUtils";
import { TableErrorState, EmptyState } from "@/components/Common/ErrorState";
import { toast } from "sonner";

function getMonitoringBadgeClassName(type: MonitoringType): string {
  const baseClasses =
    "flex h-5 w-5 items-center justify-center rounded-[6px] p-[3px] text-[14px]";
  const colorClass = MONITORING_BADGE_STYLES[type] || "bg-gray-500";

  return `${baseClasses} ${colorClass}`;
}

// Simple inline skeleton for pagination loading states
function InlineTableRowSkeleton() {
  return (
    <tr className="bg-black-900/50 hover:bg-black-900/80 transition-colors">
      <td className="px-4 py-1">
        <div className="h-4 w-full animate-pulse rounded bg-white/10" />
      </td>
      <td className="px-8 py-1">
        <div className="space-y-1">
          <div className="h-4 w-24 animate-pulse rounded bg-white/10" />
        </div>
      </td>
      <td className="px-4 py-1">
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 animate-pulse rounded bg-white/10" />
          <div className="h-4 w-24 animate-pulse rounded bg-white/10" />
        </div>
      </td>
      <td className="px-4 py-1">
        <div className="h-4 w-full animate-pulse rounded bg-white/10" />
      </td>
      <td className="px-4 py-1">
        <div className="h-4 w-12 animate-pulse rounded bg-white/10" />
      </td>
      <td className="px-4 py-1">
        <div className="h-4 w-full animate-pulse rounded bg-white/10" />
      </td>
      <td className="px-4 py-1">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 animate-pulse rounded-full bg-white/10" />
          <div className="h-4 w-16 animate-pulse rounded bg-white/10" />
        </div>
      </td>
    </tr>
  );
}

interface ActivityLogProps {
  userId: string;
  clientName?: string;
}

export default function ActivityLog({ userId, clientName }: ActivityLogProps) {
  // UI state - what user sees
  const [displayPageSize, setDisplayPageSize] = useState(10);
  const [displayCurrentPage, setDisplayCurrentPage] = useState(1);

  // Cache state - what we fetch from server
  const [cachePageSize] = useState(50); // Always fetch 50 items
  const [cacheCurrentPage, setCacheCurrentPage] = useState(1);
  const [isChangingPageSize, setIsChangingPageSize] = useState(false);
  const [cachedLogs, setCachedLogs] = useState<any[]>([]);
  const [lastPagination, setLastPagination] = useState<any>(null);

  const { data, isLoading, error, isFetching } = useGetClientActivityLog({
    userId,
    page: cacheCurrentPage,
    pageSize: cachePageSize,
  });

  // Accumulate cached data when new data arrives
  React.useEffect(() => {
    if (data?.logs) {
      if (cacheCurrentPage === 1) {
        // First page or reset - replace cache
        setCachedLogs(data.logs);
      } else {
        // Subsequent pages - append to cache
        setCachedLogs((prev) => [...prev, ...data.logs]);
      }
      setLastPagination(data.pagination);
    }
  }, [data, cacheCurrentPage]);

  // Calculate local pagination from cached data
  const getDisplayData = () => {
    if (cachedLogs.length === 0) return { logs: [], pagination: null };

    const startIndex = (displayCurrentPage - 1) * displayPageSize;
    const endIndex = startIndex + displayPageSize;
    const displayLogs = cachedLogs.slice(startIndex, endIndex);

    // Calculate pagination info for display
    const totalCachedItems = cachedLogs.length;
    const displayTotalPages = Math.ceil(totalCachedItems / displayPageSize);
    const hasNextInCache = displayCurrentPage < displayTotalPages;
    const hasPreviousInCache = displayCurrentPage > 1;

    // Check if we need more data from server
    const isNearCacheEnd = displayCurrentPage >= displayTotalPages - 1;
    const hasMoreOnServer = lastPagination?.has_next || false;

    return {
      logs: displayLogs,
      pagination: {
        current_page: displayCurrentPage,
        total_pages: hasMoreOnServer
          ? displayTotalPages + 1
          : displayTotalPages, // +1 if more on server
        page_size: displayPageSize,
        total_records: hasMoreOnServer
          ? lastPagination?.total_records || totalCachedItems
          : totalCachedItems,
        has_next: hasNextInCache || hasMoreOnServer,
        has_previous: hasPreviousInCache,
      },
      needsPrefetch: isNearCacheEnd && hasMoreOnServer,
    };
  };

  const displayData = getDisplayData();

  const handlePageSizeChange = (newPageSize: number) => {
    setDisplayPageSize(newPageSize);
    setDisplayCurrentPage(1);

    // Only reset cache if we need more data than currently cached
    const needsMoreData =
      newPageSize > cachedLogs.length && lastPagination?.has_next;

    if (needsMoreData) {
      setIsChangingPageSize(true);
      setCachedLogs([]);
      setCacheCurrentPage(1);
    }
  };

  const handlePageChange = (newPage: number) => {
    const startIndex = (newPage - 1) * displayPageSize;
    const needsNewData = startIndex >= cachedLogs.length;

    if (needsNewData && lastPagination?.has_next) {
      // Need to fetch next batch from server
      setCacheCurrentPage((prev) => prev + 1);
    }

    setDisplayCurrentPage(newPage);
  };

  // Reset loading state when data is fetched
  React.useEffect(() => {
    if (data && isChangingPageSize && !isFetching) {
      setIsChangingPageSize(false);
    }
  }, [data, isChangingPageSize, isFetching]);

  // Prefetch next batch when approaching cache boundary
  React.useEffect(() => {
    if (displayData.needsPrefetch && !isFetching) {
      setCacheCurrentPage((prev) => prev + 1);
    }
  }, [displayData.needsPrefetch, isFetching]);

  // Export functions
  const exportToCSV = () => {
    if (cachedLogs.length === 0) return;

    const headers = [
      "Job ID",
      "User Name",
      "Hash Key Name",
      "Token",
      "Endpoint",
      "Method",
      "Timestamp",
      "Status",
      "Error Message",
    ];
    const csvContent = [
      headers.join(","),
      ...cachedLogs.map((activity) =>
        [
          `"${activity.job_id}"`,
          `"${activity.user_name}"`,
          `"${activity.hash_key_name || ""}"`,
          `"${activity.api_key}"`,
          `"${activity.endpoint}"`,
          `"${activity.method}"`,
          `"${formatActivityTimestamp(activity.timestamp)}"`,
          `"${activity.status}"`,
          `"${activity.error_message || ""}"`,
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `activity-log-${clientName || "client"}-${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Activity log exported to CSV successfully");
  };

  const exportToJSON = () => {
    if (cachedLogs.length === 0) return;

    const exportData = {
      client_name: clientName || "client",
      user_id: userId,
      exported_at: new Date().toISOString(),
      total_records: cachedLogs.length,
      data: cachedLogs.map((activity) => ({
        job_id: activity.job_id,
        user_name: activity.user_name,
        hash_key_name: activity.hash_key_name,
        token: activity.api_key,
        endpoint: activity.endpoint,
        method: activity.method,
        timestamp: activity.timestamp,
        formatted_timestamp: formatActivityTimestamp(activity.timestamp),
        status: activity.status,
        error_message: activity.error_message,
      })),
    };

    const jsonContent = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonContent], {
      type: "application/json;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `activity-log-${clientName || "client"}-${new Date().toISOString().split("T")[0]}.json`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Activity log exported to JSON successfully");
  };
  if (isLoading) {
    return (
      <DashboardCard className="w-full cursor-default gap-5">
        {/* Header skeleton */}
        <div className="flex flex-row items-center justify-between">
          <div className="space-y-2">
            <div className="h-5 w-32 animate-pulse rounded bg-white/10" />
            <div className="h-3 w-48 animate-pulse rounded bg-white/10" />
          </div>
          <div className="flex gap-2">
            <div className="h-8 w-32 animate-pulse rounded bg-white/10" />
            <div className="h-8 w-32 animate-pulse rounded bg-white/10" />
          </div>
        </div>

        {/* Table skeleton */}
        <div className="scrollbar-thin overflow-x-auto">
          <table className="min-w-max table-auto border-collapse text-sm">
            <thead>
              <tr className="text-black-500 text-left text-xs font-medium tracking-wide uppercase">
                <th className="px-4 py-3">
                  <div className="h-3 w-16 animate-pulse rounded bg-white/10" />
                </th>
                <th className="px-8 py-3">
                  <div className="h-3 w-12 animate-pulse rounded bg-white/10" />
                </th>
                <th className="px-4 py-3">
                  <div className="h-3 w-12 animate-pulse rounded bg-white/10" />
                </th>
                <th className="px-4 py-3">
                  <div className="h-3 w-20 animate-pulse rounded bg-white/10" />
                </th>
                <th className="px-4 py-3">
                  <div className="h-3 w-16 animate-pulse rounded bg-white/10" />
                </th>
                <th className="px-4 py-3">
                  <div className="h-3 w-20 animate-pulse rounded bg-white/10" />
                </th>
                <th className="px-4 py-3">
                  <div className="h-3 w-14 animate-pulse rounded bg-white/10" />
                </th>
              </tr>
            </thead>
            <tbody>
              {[...Array(3)].map((_, i) => (
                <tr
                  key={`skeleton-${i}`}
                  className="bg-black-900/50 hover:bg-black-900/80 transition-colors"
                >
                  <td className="px-4 py-1">
                    <div className="h-4 w-full animate-pulse rounded bg-white/10" />
                  </td>
                  <td className="px-8 py-1">
                    <div className="space-y-1">
                      <div className="h-4 w-24 animate-pulse rounded bg-white/10" />
                    </div>
                  </td>
                  <td className="px-4 py-1">
                    <div className="flex items-center gap-2">
                      <div className="h-5 w-5 animate-pulse rounded bg-white/10" />
                      <div className="h-4 w-24 animate-pulse rounded bg-white/10" />
                    </div>
                  </td>
                  <td className="px-4 py-1">
                    <div className="h-4 w-full animate-pulse rounded bg-white/10" />
                  </td>
                  <td className="px-4 py-1">
                    <div className="h-4 w-12 animate-pulse rounded bg-white/10" />
                  </td>
                  <td className="px-4 py-1">
                    <div className="h-4 w-full animate-pulse rounded bg-white/10" />
                  </td>
                  <td className="px-4 py-1">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 animate-pulse rounded-full bg-white/10" />
                      <div className="h-4 w-16 animate-pulse rounded bg-white/10" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination skeleton */}
        <div className="flex flex-row items-center justify-between">
          <div className="h-4 w-48 animate-pulse rounded bg-white/10" />
          <div className="flex items-center gap-2">
            <div className="h-8 w-24 animate-pulse rounded bg-white/10" />
            <div className="h-8 w-8 animate-pulse rounded bg-white/10" />
            <div className="h-8 w-8 animate-pulse rounded bg-white/10" />
          </div>
          <div className="h-8 w-32 animate-pulse rounded bg-white/10" />
        </div>
      </DashboardCard>
    );
  }

  if (error) {
    return (
      <TableErrorState
        message="Failed to load activity log. Please try again."
        onRetry={() => window.location.reload()}
      />
    );
  }

  if (!data || (cachedLogs.length === 0 && !isLoading && !isChangingPageSize)) {
    return (
      <DashboardCard className="w-full gap-5">
        <div className="space-y-2">
          <div className="text-lg leading-5 font-bold -tracking-wide text-white">
            Activity Log
          </div>
          <div className="text-black-500 text-xs leading-3">
            Recent activity for {clientName || "client"}
          </div>
        </div>
        <EmptyState
          icon={
            <i className="ri-file-list-3-line text-black-500 mb-2 text-4xl" />
          }
          title="No activity logs found"
          message="There are no activity logs to display for this client at this time."
          isDarkMode={true}
        />
      </DashboardCard>
    );
  }

  return (
    <DashboardCard className="w-full cursor-default gap-5">
      {/* Header */}
      <div className="flex flex-row items-center justify-between">
        <div className="space-y-2">
          <div className="text-lg leading-5 font-bold -tracking-wide text-white">
            Activity Log
          </div>
          <div className="text-black-500 text-xs leading-3">
            Recent activity for {clientName || "client"}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            className="gap-2 p-1.5 text-xs leading-normal font-bold tracking-normal text-white hover:bg-white/30 hover:text-white"
            variant="minimal"
            icon
            onClick={exportToCSV}
            disabled={cachedLogs.length === 0}
          >
            <i className="ri-file-excel-2-line text-xs font-light"></i>
            Export as CSV
          </Button>
          <Button
            className="gap-2 p-1.5 text-xs leading-normal font-bold tracking-normal text-white hover:bg-white/30 hover:text-white"
            variant="minimal"
            icon
            onClick={exportToJSON}
            disabled={cachedLogs.length === 0}
          >
            <i className="ri-braces-line text-xs font-light"></i>
            Export as JSON
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="scrollbar-thin overflow-x-auto">
        <table className="w-full min-w-max table-auto border-collapse text-sm">
          <thead>
            <tr className="text-black-500 text-left text-xs font-medium tracking-wide uppercase">
              <th className="px-4 py-3">Job ID</th>
              <th className="px-8 py-3">User</th>
              <th className="px-4 py-3">Token</th>
              <th className="px-4 py-3">Endpoint</th>
              <th className="px-4 py-3">Method</th>
              <th className="px-4 py-3">Timestamp</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {(isFetching || isChangingPageSize) && cachedLogs.length > 0
              ? // Show skeleton rows with the new page size count during loading
                [...Array(displayPageSize)].map((_, i) => (
                  <InlineTableRowSkeleton key={`skeleton-${i}`} />
                ))
              : displayData.logs.map((activity) => {
                  // Check if token is premium
                  const isPremium =
                    activity.token?.toLowerCase()?.includes("premium") ?? false;

                  const monitoringType: MonitoringType =
                    getMonitoringTypeFromToken(activity.api_key);
                  const monitoringIcon = createMonitoringIcon(
                    monitoringType,
                    "text-sm",
                    true,
                  );
                  const statusColor = getActivityStatusColor(activity.status);

                  const isHashKey =
                    activity.hash_key_name &&
                    activity.hash_key_name !== "No Hash Key";

                  return (
                    <tr
                      key={activity.job_id}
                      className="bg-black-900/50 hover:bg-black-900/80 transition-colors"
                    >
                      <td className="px-4 py-1 text-xs leading-4 font-medium -tracking-wide text-white">
                        <div className="whitespace-nowrap">
                          {activity.job_id}
                        </div>
                      </td>
                      <td className="px-8 py-1">
                        <div className="space-y-1">
                          <div className="text-xs leading-4 font-medium -tracking-wide whitespace-nowrap text-white">
                            {isHashKey ? (
                              <div className="flex items-center gap-1.5">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <i className="ri-key-line text-md text-white"></i>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="left"
                                    className="border-dark-border border bg-black"
                                  >
                                    <p>Hash Key</p>
                                  </TooltipContent>
                                </Tooltip>
                                <div className="text-xs leading-4 font-medium -tracking-wide text-white">
                                  {activity.hash_key_name}
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <i className="ri-admin-line text-md text-white"></i>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="left"
                                    className="border-dark-border border bg-black"
                                  >
                                    <p>Manager</p>
                                  </TooltipContent>
                                </Tooltip>
                                <div className="text-xs leading-4 font-medium -tracking-wide text-white">
                                  {activity.user_name}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-1">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          {isPremium ? (
                            <div className="flex h-5 w-5 items-center justify-center rounded-[6px] bg-yellow-500 p-[3px]">
                              <i className="ri-star-line text-xs text-white"></i>
                            </div>
                          ) : (
                            <div
                              className={getMonitoringBadgeClassName(
                                monitoringType,
                              )}
                            >
                              {monitoringIcon}
                            </div>
                          )}
                          <span className="text-xs leading-4 font-medium -tracking-wide text-white">
                            {activity.api_key}
                          </span>
                        </div>
                      </td>
                      <td className="max-w-[200px] truncate px-4 py-1 text-xs leading-4 font-medium -tracking-wide text-white">
                        <div className="truncate whitespace-nowrap">
                          {activity.endpoint}
                        </div>
                      </td>
                      <td className="px-4 py-1 text-xs leading-4 font-medium -tracking-wide text-white">
                        <div className="whitespace-nowrap">
                          {activity.method}
                        </div>
                      </td>
                      <td className="px-4 py-1 text-xs leading-4 font-medium -tracking-wide text-white">
                        <div className="whitespace-nowrap">
                          {formatActivityTimestamp(activity.timestamp)}
                        </div>
                      </td>
                      <td className="px-4 py-1">
                        {activity.error_message ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex cursor-help items-center gap-2 whitespace-nowrap">
                                <span
                                  className="h-2 w-2 flex-shrink-0 rounded-full"
                                  style={{ backgroundColor: statusColor }}
                                />
                                <span className="py-1 text-xs leading-4 font-medium -tracking-wide text-white">
                                  {activity.status}
                                </span>
                                <i className="ri-error-warning-line text-xs text-red-500"></i>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent
                              side="left"
                              className="border-dark-border max-w-xs border bg-black"
                            >
                              <p className="text-xs">
                                {activity.error_message}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            <span
                              className="h-2 w-2 flex-shrink-0 rounded-full"
                              style={{ backgroundColor: statusColor }}
                            />
                            <span className="py-1 text-xs leading-4 font-medium -tracking-wide text-white">
                              {activity.status}
                            </span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {displayData.pagination && (
        <Pagination
          currentPage={displayData.pagination.current_page}
          totalPages={displayData.pagination.total_pages}
          pageSize={displayData.pagination.page_size}
          totalRecords={displayData.pagination.total_records}
          hasNext={displayData.pagination.has_next}
          hasPrevious={displayData.pagination.has_previous}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </DashboardCard>
  );
}
